import { Button, Grid, Hidden, Link, Theme, useMediaQuery, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import { get as _get } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { converseNext, createDraftClaim } from '../../../api/converseApi';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import {
  addDriver,
  addFnolReporter,
  addFnolRisk,
  saveDateLocation,
  updateDraftPayload,
} from '../../../app/store/ClaimCreation/ClaimCreationSlice';
import { addDraftClaim } from '../../../app/store/Claims/ClaimsSlice';
import {
  getAuthKey,
  getClaimCreationProductType,
  getClaimRisks,
  getFNOLDateTime,
  getMobileSourceSystem,
  getQuestionSetPartner,
  getSelectedRiskPolicyNumber,
  getSessionId,
  getUrlKey,
  getUserData,
} from '../../../app/store/combinedSelectors';
import { updateAuthToken } from '../../../app/store/ContextRouter/ContextRouterActions';
import { cancelBtn, continueWithNewClaimBtn, resumeBtn, viewDetailsBtn } from '../../../Constants/Constants';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import {
  buildDraftClaimPayload,
  createInitiateDynamicQuestionPayload,
  mapLocalDraftToCCFormat,
  setupDraftResumeFields,
} from '../../../services/SetupFnol.service';
import { setupPostFnol } from '../../../services/SetupPostFnol.service';
import { GenericProductType } from '../../../types/type';
import { getClaimDescription } from '../../../utils/Policy-Util';
import { getPolicySource } from '../../../utils/PolicySource-Util';
import { isPolicyTypeAuto, postMessageToParentApp } from '../../../utils/Utilities';
import CallToAction from '../../shared/CallToAction/CallToAction';
import ClaimDescriptionLink from '../../shared/ClaimDescriptionLink/ClaimDescriptionLink';
import ErrorBanner from '../../shared/ErrorBanner/ErrorBanner';
import ModalLinearActivityIndicator from '../../shared/ModalLinearActivityIndicator/ModalLinearActivityIndicator';
import ProgressTrackerMini from '../../shared/ProgressTracker/ProgressTrackerMini';
import ClaimList from '../claims-list/ClaimsList';

export default function ExistingClaims() {
  const theme: Theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const opco = useSelector(getQuestionSetPartner);
  const dupClaims = _get(useLocation(), 'state.dupClaims');
  const draftsAndFull = _get(useLocation(), 'state.draftsAndFull');
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const customerData = useSelector(getUserData);
  const dateTimeData = useSelector(getFNOLDateTime);
  const fnolData = useAppSelector((state) => state.claimCreation);
  const typeOfClaim = useSelector(getClaimCreationProductType);
  const urlKey = useSelector(getUrlKey);
  const sessionId = useSelector(getSessionId);
  const authKey = useSelector(getAuthKey);
  const claimRisks = useSelector(getClaimRisks);
  const mobileSourceSystem = useSelector(getMobileSourceSystem);
  const policies = useAppSelector((state) => state.policies.policies);
  const selectedPolicyNumber = useSelector(getSelectedRiskPolicyNumber);

  /*
  This variable is introduced to address the scenario where, on desktop browsers, the styling requirements 
  for the operating company (opco) dictate that the back button should appear as a link (specific to AmFam).
  On this particular screen, the back button needs to function as a tertiary button, 
  but visually resemble a skip button. This variable will be used to conditionally render the 
  back button as a tertiary button.
  */
  const shouldShowSkipButton = !isMobile && theme.custom.styles.ctaBackButtonIsLinkOnDesktop;
  const buttonProps = shouldShowSkipButton
    ? {
        skipButton: true,
        skipButtonContent: continueWithNewClaimBtn,
        handleSkip: handleNext,
      }
    : {
        backButton: true,
        backButtonContent: continueWithNewClaimBtn,
        handleBack: handleNext,
      };
  const isAuto = typeOfClaim === GenericProductType.Auto;

  const [modalLoadingOpen, setModalLoadingOpen] = React.useState(false);
  const [showErrorBanner, setShowErrorBanner] = React.useState(false);
  const cardStyle = {
    border: `${theme.custom.styles.defaultBorderWidth} solid ${theme.custom.colors.defaultBorderColor}`,
    borderRadius: theme.custom.styles.defaultBorderRadius,
    margin: '1rem 0rem 1rem 0rem',
  };

  async function handleNext() {
    setShowErrorBanner(false);
    setModalLoadingOpen(true);
    const draftClaimBody = buildDraftClaimPayload(customerData, fnolData, dateTimeData, opco, mobileSourceSystem, {
      creatingDraft: true,
    });

    // Calculate headers once to ensure consistency between createDraftClaim and converseNext calls
    const lob = isAuto ? 'auto' : 'property';
    let policySource = '';
    if (isAuto) {
      policySource = getPolicySource(selectedPolicyNumber, policies);
    }

    // Pass headers to createDraftClaim so API can use them for its converseNext call
    // This ensures the API and any UI fallback converseNext calls use identical headers
    const response = await createDraftClaim(
      urlKey,
      sessionId,
      authKey,
      draftClaimBody,
      lob, // line-of-business header
      opco, // partner-id header (will be mapped in createDraftClaim)
      policySource // policy-source header
    );

    if (response.claimNumber && response.claimNumber.length > 0) {
      const converseDraftClaim = buildDraftClaimPayload(
        customerData,
        fnolData,
        dateTimeData,
        opco,
        mobileSourceSystem,
        { creatingDraft: false }
      );

      const converseBody = createInitiateDynamicQuestionPayload(response.claimNumber, converseDraftClaim);

      // Check if the draft claim creation API returned converse data
      // If so, use it directly instead of making an additional API call
      const converseData = _get(response, 'converseData', null);
      const converseError = _get(response, 'converseError', null);

      let converseResponse;

      if (converseData && _get(converseData, 'status.code', 0) >= 200) {
        // Happy path: Use the converse data from the draft claim creation response
        // No additional API call needed since API already called converseNext successfully
        converseResponse = {
          data: converseData,
        };
        setModalLoadingOpen(false);
      } else if (converseError) {
        // Fallback: Converse initialization failed in the API, make the call from UI
        // Use the same lob, opco, and policySource we sent to createDraftClaim
        // This ensures consistency in header values between API and UI calls
        converseResponse = await converseNext(
          urlKey,
          sessionId,
          authKey,
          lob,
          opco,
          converseBody.draftClaimNumber,
          converseBody.payload,
          policySource
        );
        setModalLoadingOpen(false);
      } else {
        // Legacy fallback: if API doesn't include converse data, make the call
        // This shouldn't happen after Phase 1 API deployment, but maintained for safety
        converseResponse = await converseNext(
          urlKey,
          sessionId,
          authKey,
          lob,
          opco,
          converseBody.draftClaimNumber,
          converseBody.payload,
          policySource
        );
        setModalLoadingOpen(false);
      }

      if (_get(converseResponse, 'data.status.code', '') >= 200) {
        dispatch(updateAuthToken(converseResponse.data.updatedAuthToken));
        const ccDraftClaim = mapLocalDraftToCCFormat(draftClaimBody, converseBody.draftClaimNumber);
        dispatch(addDraftClaim({ draft: ccDraftClaim }));
        dispatch(updateDraftPayload({ draftPayload: converseDraftClaim }));

        navigate('/dynamic-question', {
          state: {
            activeQuestion: converseResponse.data.response,
            draftClaimNumber: response.claimNumber,
            isAuto: isAuto,
          },
        });
      } else {
        setModalLoadingOpen(false);
        postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_CONVERSE_NEXT_ERROR, '', converseResponse);
        setShowErrorBanner(true);
      }
    } else {
      setModalLoadingOpen(false);
      postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_CONVERSE_NEXT_ERROR, '', response);
      setShowErrorBanner(true);
    }
  }

  async function resumeClaim(claim: any) {
    setModalLoadingOpen(true);
    const lob = isPolicyTypeAuto(claim) ? 'auto' : 'property';

    // Send the policy-source header in requests to Converse to enable differentiation of question set retrieval between CAH AUTO and other OPCO lines of business (LOBs).
    let policySource = '';
    if (isPolicyTypeAuto(claim)) {
      policySource = getPolicySource(selectedPolicyNumber, policies);
    }

    const converseResponse = await converseNext(
      urlKey,
      sessionId,
      authKey,
      lob,
      opco,
      _get(claim, 'name', _get(claim, 'claimNumber', '')).replace(/-/g, ''),
      {},
      policySource
    );
    setModalLoadingOpen(false);

    if (_get(converseResponse, 'data.status.code', '') >= 200) {
      const claimCreationState = setupDraftResumeFields(converseResponse.data.response, claimRisks);
      dispatch(addFnolRisk({ riskInfo: claimCreationState.riskInfo }));

      dispatch(addFnolReporter({ reporterInfo: claimCreationState.reporterInfo }));
      dispatch(addDriver({ driverInfo: claimCreationState.driverInfo }));
      dispatch(
        saveDateLocation({
          dateLocationInfo: claimCreationState.dateLocationInfo,
        })
      );
      dispatch(updateAuthToken(converseResponse.data.updatedAuthToken));
      dispatch(
        updateDraftPayload({
          draftPayload: converseResponse.data.response.draftClaimInfo,
        })
      );

      navigate('/dynamic-question', {
        state: {
          activeQuestion: converseResponse.data.response,
          draftClaimNumber: _get(claim, 'name', _get(claim, 'claimNumber', '')).replace(/-/g, ''),
          isAuto: isPolicyTypeAuto(claim),
          reviewSheet: _get(converseResponse, 'data.response.question.reviewSheet'),
        },
      });
    } else {
      setModalLoadingOpen(false);
      postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_CONVERSE_NEXT_ERROR, '', converseResponse);
      setShowErrorBanner(true);
    }
  }

  async function goToDetail(claim: any) {
    setModalLoadingOpen(true);
    const claimNumber = _get(claim, 'name', _get(claim, 'claimNumber', '')).replace(/-/g, '');
    const successfulSetup = await setupPostFnol(
      customerData,
      claimNumber,
      urlKey,
      sessionId,
      authKey,
      true,
      opco,
      'existing-claims'
    );
    setModalLoadingOpen(false);

    if (successfulSetup === GenericProductType.Auto || successfulSetup === GenericProductType.Home) {
      navigate('/claim-detail');
    } else {
      postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_CLAIM_DETAIL_FAIL_ERROR, claimNumber, successfulSetup);
      setShowErrorBanner(true);
    }
  }

  async function handleCancel() {
    navigate('/claims-overview');
  }

  return (
    <Grid
      container
      sx={{
        width: '100%',
        maxWidth: '1280px',
        minWidth: '310px',
        margin: '0 auto',
        marginTop: '1em',
      }}
    >
      {showErrorBanner && <ErrorBanner></ErrorBanner>}
      {modalLoadingOpen && <ModalLinearActivityIndicator />}

      <Grid item xs={12}>
        <Typography
          sx={{
            ...theme.custom.styles.titleType4,
          }}
        >
          A similar claim has been found!
        </Typography>
      </Grid>

      <Grid sx={{ marginTop: '1em' }} item xs={12}>
        <Typography variant="body1">
          You already have a similar claim! <br /> Select which claim you would like to continue with below, or continue
          reporting a new claim.
        </Typography>
      </Grid>

      <Hidden smUp>
        {!!dupClaims && dupClaims.length > 0 && (
          <>
            {dupClaims.map((draft: any) => (
              <Grid container sx={cardStyle} key={draft.name ? draft.name : draft.claimNumber}>
                <Grid
                  item
                  xs={12}
                  sx={{
                    px: '1em',
                    paddingTop: '1em',
                  }}
                >
                  <Link
                    component="button"
                    underline="hover"
                    sx={{
                      ...theme.custom.styles.titleType4,
                      textAlign: 'left',
                      whiteSpace: 'pre-line',
                    }}
                    onClick={() => (draft.isFullClaim ? goToDetail(draft) : resumeClaim(draft))}
                  >
                    {draft.isFullClaim ? draft.name || 'Saved Draft' : 'Saved Draft'} -{' '}
                    <ClaimDescriptionLink description={getClaimDescription(draft)} />
                  </Link>
                </Grid>

                <Grid
                  item
                  xs={4}
                  sx={{
                    paddingLeft: '1em',
                    paddingTop: '1em',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body1">
                    <b>Incident Date</b>
                  </Typography>
                </Grid>

                <Grid item xs={8} sx={{ paddingTop: '1em' }}>
                  <Typography variant="body1">{draft.lossDate}</Typography>
                </Grid>

                <Grid
                  item
                  xs={4}
                  sx={{
                    paddingLeft: '1em',

                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body1">
                    <b> Status</b>
                  </Typography>
                </Grid>

                <Grid item xs={8} sx={{ paddingTop: '1em' }}>
                  <Typography style={{ width: '33%', textAlign: 'left' }} variant="body1">
                    Submit
                  </Typography>

                  <ProgressTrackerMini marginLeft={'0%'} step={'Submit'} name={draft.name} />
                </Grid>

                <Grid
                  item
                  xs={12}
                  sx={{
                    paddingTop: '1em',
                    paddingBottom: '1em',
                    px: '1em',
                    flexDirection: 'row',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  {!!draftsAndFull && !draft.isFullClaim && (
                    <Button
                      onClick={() => (draft.isFullClaim ? goToDetail(draft) : resumeClaim(draft))}
                      sx={{
                        ...theme.custom.styles.genericPrimaryButton,
                      }}
                    >
                      {resumeBtn}
                    </Button>
                  )}

                  {!draftsAndFull ||
                    (!!draft.isFullClaim && (
                      <Button
                        onClick={() => (draft.isFullClaim ? goToDetail(draft) : resumeClaim(draft))}
                        sx={{
                          ...theme.custom.styles.genericPrimaryButton,
                        }}
                      >
                        {draft.isFullClaim ? viewDetailsBtn : resumeBtn}
                      </Button>
                    ))}
                </Grid>
              </Grid>
            ))}
          </>
        )}
      </Hidden>

      <Hidden smDown>
        {!!dupClaims && dupClaims.length > 0 && (
          <Grid item xs={12} sx={{ marginTop: '2em' }}>
            <ClaimList
              hideDelete={true}
              combinedList={true}
              handleNext={(draft: any) => (draft.isFullClaim ? goToDetail(draft) : resumeClaim(draft))}
              isDraft={false}
              claims={dupClaims}
              draftsAndFull={draftsAndFull}
            />
          </Grid>
        )}
      </Hidden>

      <Grid item xs={12}>
        <CallToAction
          nextButton={false}
          cancelButton={true}
          cancelButtonContent={cancelBtn}
          handleCancel={handleCancel}
          {...buttonProps}
        ></CallToAction>
      </Grid>
    </Grid>
  );
}
